import { useEffect, useMemo } from 'react'
import { RouteProps } from 'react-router-dom'
import {
  isLoading,
  LoadingTypes,
  useAccountId,
  useCloud,
  useCurrentCloudProvider,
  useSyncCloudMarketplace,
  useTenantPartnerId,
  useValidateCloudRoute,
} from '@labrav/common'
import { PlanType } from '@labrav/react-components'
import { useFlag } from '@labrav/flags'
import { isEmpty } from 'lodash'

import { getProductDetail } from '../../../modules/addEditProduct/action'
import { getFlyOutOnboardingData } from '../../../modules/flyOutOnboarding/action'
import { useDispatch, useSelector } from '../../../store'
import {
  allowedCopyProduct,
  type ProductDefaultInfo,
} from '../../../types/addEditProduct'
import { CloudType } from '../../../types/index'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { useSyncFlyoutRouteParams } from '../../utils/hooks/useSyncFlyoutRouteParams'

const allowedProductTypeForCopy = [
  PlanType.SAAS_CONTRACT,
  PlanType.SAAS_SUBSCRIPTION,
]

type FlyoutSubscribedProducts = {
  FLYOUT?: {
    cloudProviders?: string[]
  }
}

export const FlyoutProductsWrapper: React.FC<RouteProps> = ({ children }) => {
  const dispatch = useDispatch()

  // Sync route params to selectedAccountIds for FLYOUT - single source of truth
  useSyncFlyoutRouteParams()

  // Sync Redux cloud marketplace state with useCloud value
  useSyncCloudMarketplace()

  // Validate cloud type and redirect if unsupported (e.g., RedHat -> AWS/Azure/GCP)
  // This ensures all routes under FlyoutProductsWrapper validate cloud types
  useValidateCloudRoute('flyout', '/flyout', false)

  // Get accountId from selectedAccountIds[FLYOUT] (single source of truth)
  // Don't pass cloud parameter - FLYOUT only has one cloud at a time
  const accountId = useAccountId({ product: 'flyout' })

  // Get the current cloud for FLYOUT
  const flyoutCloud = useCloud({ product: 'flyout', accountId })

  const tenantPartnerId = useTenantPartnerId() ?? ''

  const { flag: isMicrosoftResaleAuthorizationsEnabled } = useFlag(
    'microsoftResellerAuthorization'
  )

  const cloudMarketplace = useCurrentCloudProvider({
    microsoftResellerAuthorization:
      isMicrosoftResaleAuthorizationsEnabled.value,
  })

  const { products } = useFlyOutProductsBasedOnSelectedCloud()

  const isLoadingGeneral = useSelector(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )

  const productDefaultInfo = useSelector(
    state => state?.flyoutProductInCreation?.productDefaultInfo
  ) as ProductDefaultInfo | undefined
  const partnerData = useSelector(state => state.AccountData.user.partnerData)

  const flyoutProduct = (
    partnerData?.subscribedProducts as FlyoutSubscribedProducts | undefined
  )?.FLYOUT
  const flyoutSubscribedCloudProviders = useMemo(
    () => flyoutProduct?.cloudProviders || [],
    [flyoutProduct?.cloudProviders]
  )

  useEffect(() => {
    // Use the actual cloud from selectedAccountIds, or fallback to cloudMarketplace
    const currentCloud = (flyoutCloud || cloudMarketplace) as CloudType

    // Check if the current cloud is in the subscribed cloud providers
    // cloudProviders is an array like ['AWS', 'AZURE', 'GCP']
    const currentCloudString = currentCloud?.toString()?.toUpperCase()
    if (
      accountId &&
      currentCloud &&
      currentCloudString &&
      flyoutSubscribedCloudProviders.some(
        provider => provider?.toUpperCase() === currentCloudString
      )
    ) {
      dispatch(
        getFlyOutOnboardingData(accountId, tenantPartnerId, currentCloud, true)
      )
    }
  }, [
    accountId,
    flyoutCloud,
    cloudMarketplace,
    tenantPartnerId,
    flyoutSubscribedCloudProviders,
  ])

  useEffect(() => {
    if (
      accountId &&
      cloudMarketplace === 'AWS' &&
      products?.length &&
      !isLoadingGeneral &&
      isEmpty(productDefaultInfo)
    ) {
      const sortedProducts = products
        ?.sort(function (a, b) {
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        })
        .filter(
          productItem =>
            productItem.productType &&
            allowedProductTypeForCopy.includes(productItem.productType)
        )
      const productId = sortedProducts?.find(productItem =>
        allowedCopyProduct.includes(productItem?.status)
      )?.productId
      productId &&
        dispatch(
          getProductDetail({
            partnerId: accountId,
            isDefault: true,
            productId: productId,
          })
        )
    }
  }, [
    accountId,
    cloudMarketplace,
    products,
    isLoadingGeneral,
    productDefaultInfo,
  ])

  return <>{children}</>
}
